const defaultMaxInputBytes = 14 * 1024 * 1024;
const defaultMaxDataUrlChars = 1_050_000;
const defaultMaxDimension = 1600;
const qualities = [0.82, 0.74, 0.66, 0.58, 0.5, 0.42];
const dimensions = [1600, 1400, 1200, 1000, 850];

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read image."));
    reader.readAsDataURL(file);
  });
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not open image. Please try another photo."));
    };
    img.src = url;
  });
}

function canvasToDataUrl(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          reject(new Error("Could not compress image."));
          return;
        }
        try {
          resolve(await readFileAsDataUrl(blob));
        } catch (e) {
          reject(e);
        }
      },
      "image/jpeg",
      quality,
    );
  });
}

function drawImageToCanvas(img, maxDimension) {
  const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

export async function readChatImage(file, options = {}) {
  if (!file) return null;
  if (!file.type?.startsWith("image/")) throw new Error("Please choose an image file.");

  const maxInputBytes = options.maxInputBytes || defaultMaxInputBytes;
  const maxDataUrlChars = options.maxDataUrlChars || defaultMaxDataUrlChars;
  if (file.size > maxInputBytes) {
    throw new Error("Image is too large. Please use a photo under 14 MB.");
  }

  const original = await readFileAsDataUrl(file);
  if (original.length <= maxDataUrlChars) {
    return { dataUrl: original, name: file.name };
  }

  const img = await loadImage(file);
  const sizes = [options.maxDimension || defaultMaxDimension, ...dimensions].filter((value, index, arr) => (
    value && arr.indexOf(value) === index
  ));

  let smallest = "";
  for (const size of sizes) {
    const canvas = drawImageToCanvas(img, size);
    for (const quality of qualities) {
      const dataUrl = await canvasToDataUrl(canvas, quality);
      if (!smallest || dataUrl.length < smallest.length) smallest = dataUrl;
      if (dataUrl.length <= maxDataUrlChars) {
        return { dataUrl, name: file.name };
      }
    }
  }

  if (smallest && smallest.length <= Math.ceil(maxDataUrlChars * 1.08)) {
    return { dataUrl: smallest, name: file.name };
  }
  throw new Error("Image is too large to send. Please crop it or choose a smaller photo.");
}
